/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { truncateMarkdown } from '../../../../utils/string/truncateMarkdown';
import ReactMarkdown from 'react-markdown';

// Component imports
import { Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import PriorityTag from '../../PriorityTag';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Paragraph, Text } = Typography;

const OptionNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { currentBotLanguage } = useBotLanguage();
  const { graphDirection } = useContext(GraphContext);

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'} />

        <span size={6} className="node-header">
          {nodeProps.icon}
        </span>

        {
          data.label[currentBotLanguage] ? (
            <Paragraph className="m-0" ellipsis={{ rows: 3, expandable: true, symbol: ' ' }}>
              <ReactMarkdown
                skipHtml
                linkTarget="_blank"
              >
                {
                  truncateMarkdown(data.label[currentBotLanguage], {
                    limit: 50,
                    ellipsis: true,
                    replaceImages: true,
                    replaceBlankSpaces: true
                  })
                }
              </ReactMarkdown>
            </Paragraph>
          ) : (
            <Text type="secondary">
              {t('Write some content')}
            </Text>
          )
        }

        <LimitedConnectionsHandle id={id} type="source" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'} allowedOutgoingEdgesCount={1} />

        <PriorityTag nodeId={id} currentPriority={data.priority} />
      </Fragment>
    );
  }, [id, data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
OptionNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(OptionNode);
